import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { MessageSquare, Loader2, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { recordingModeActive } from "@/lib/recordingMode";
import { getNavTrail, getTapTrail, parseDeviceFromUA, cleanPath } from "@/lib/navTrail";
import { getErrorTrail } from "@/lib/errorTrail";

const POSITION_STORAGE_KEY = "feedbackWidget:position";
const DRAG_THRESHOLD_PX = 6;
const EDGE_PADDING = 8;

type WidgetPosition = { x: number; y: number };

function loadSavedPosition(): WidgetPosition | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function clampToViewport(pos: WidgetPosition, width: number, height: number): WidgetPosition {
  const maxX = Math.max(EDGE_PADDING, window.innerWidth - width - EDGE_PADDING);
  const maxY = Math.max(EDGE_PADDING, window.innerHeight - height - EDGE_PADDING);
  return {
    x: Math.min(Math.max(EDGE_PADDING, pos.x), maxX),
    y: Math.min(Math.max(EDGE_PADDING, pos.y), maxY),
  };
}

const MAX_SCREENSHOTS = 3;
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

// Canvas-resize + re-encode to JPEG. iPhone HEIC files may not decode via
// createImageBitmap on all browsers; errors bubble up to the caller.
async function resizeImageToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Image encode failed"))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
  return await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("Image read failed"));
    r.readAsDataURL(blob);
  });
}

type PublicConfig = {
  demoMode: boolean;
  realModeLaunchDate: string | null;
};

type FeedbackCategory = "bug" | "idea" | "praise" | "other";

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: "idea", label: "Idea" },
  { value: "bug", label: "Bug" },
  { value: "praise", label: "Praise" },
  { value: "other", label: "Other" },
];

async function captureDiagnostics() {
  if (typeof window === "undefined") return null;
  let platform: string = "web";
  try {
    platform = Capacitor.getPlatform();
  } catch {
    /* fallback to "web" */
  }
  const orientation = (() => {
    try {
      return window.screen.orientation?.type ?? null;
    } catch {
      return null;
    }
  })();
  const ua = navigator.userAgent ?? "";
  const nav = navigator as any;
  const conn = nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
  const perf = window.performance;
  const mem = (perf as any)?.memory;
  const sessionStart = perf?.timeOrigin ? Math.round((Date.now() - perf.timeOrigin) / 1000) : null;

  let swStatus: string = "unsupported";
  try {
    if ("serviceWorker" in navigator) {
      const reg = navigator.serviceWorker.controller;
      swStatus = reg ? `active (${reg.scriptURL.split("/").pop()})` : "registered_no_controller";
    }
  } catch { swStatus = "error"; }

  let storageInfo: { usedMB?: number; quotaMB?: number } | null = null;
  try {
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      storageInfo = {
        usedMB: est.usage ? Math.round(est.usage / 1048576) : undefined,
        quotaMB: est.quota ? Math.round(est.quota / 1048576) : undefined,
      };
    }
  } catch { /* ignore */ }

  let localStorageCount = 0;
  let sessionStorageCount = 0;
  try { localStorageCount = window.localStorage.length; } catch { /* ignore */ }
  try { sessionStorageCount = window.sessionStorage.length; } catch { /* ignore */ }

  return {
    platform,
    device: parseDeviceFromUA(ua),
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen.width, height: window.screen.height },
    devicePixelRatio: window.devicePixelRatio,
    orientation,
    language: navigator.language ?? null,
    online: navigator.onLine,
    connection: conn ? {
      type: conn.type ?? null,
      effectiveType: conn.effectiveType ?? null,
      downlink: conn.downlink ?? null,
      rtt: conn.rtt ?? null,
      saveData: conn.saveData ?? false,
    } : null,
    memory: mem ? {
      usedMB: Math.round(mem.usedJSHeapSize / 1048576),
      totalMB: Math.round(mem.totalJSHeapSize / 1048576),
      limitMB: Math.round(mem.jsHeapSizeLimit / 1048576),
    } : null,
    sessionDurationSec: sessionStart,
    serviceWorker: swStatus,
    storage: storageInfo,
    localStorageKeys: localStorageCount,
    sessionStorageKeys: sessionStorageCount,
    capturedAt: new Date().toISOString(),
    navTrail: getNavTrail().map((e) => ({ ...e, path: cleanPath(e.path) })),
    tapTrail: getTapTrail(),
    errorTrail: getErrorTrail(),
  };
}

export function FeedbackWidget() {
  const { data: config } = useQuery<PublicConfig>({
    queryKey: ["/api/public/config"],
    queryFn: async () => {
      const res = await fetch("/api/public/config", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [position, setPosition] = useState<WidgetPosition | null>(() => loadSavedPosition());
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dragState = useRef<{
    pointerStartX: number;
    pointerStartY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    if (!position) return;
    const el = buttonRef.current;
    if (!el) return;
    const clamped = clampToViewport(position, el.offsetWidth, el.offsetHeight);
    if (clamped.x !== position.x || clamped.y !== position.y) {
      setPosition(clamped);
    }
  }, [position]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      pointerStartX: e.clientX,
      pointerStartY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = dragState.current;
    if (!s) return;
    const dx = e.clientX - s.pointerStartX;
    const dy = e.clientY - s.pointerStartY;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    s.moved = true;
    didDragRef.current = true;
    const el = e.currentTarget;
    const next = clampToViewport(
      { x: s.originX + dx, y: s.originY + dy },
      el.offsetWidth,
      el.offsetHeight,
    );
    setPosition(next);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = dragState.current;
    dragState.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (s?.moved) {
      try {
        const el = buttonRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          window.localStorage.setItem(
            POSITION_STORAGE_KEY,
            JSON.stringify({ x: rect.left, y: rect.top }),
          );
        }
      } catch {
        /* ignore */
      }
    }
  };

  const onButtonClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    setOpen(true);
  };

  const handleAttachFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const room = MAX_SCREENSHOTS - screenshots.length;
    if (room <= 0) return;
    setAttaching(true);
    try {
      const picked = Array.from(fileList).slice(0, room);
      const resized = await Promise.all(
        picked.map(async (f) => {
          try {
            return await resizeImageToDataUrl(f);
          } catch (err: any) {
            toast({
              title: "Could not attach image",
              description: err?.message || "This image format may not be supported. Try JPEG or PNG.",
              variant: "destructive",
            });
            return null;
          }
        }),
      );
      setScreenshots((prev) => [...prev, ...resized.filter((x): x is string => Boolean(x))]);
    } finally {
      setAttaching(false);
    }
  };

  useEffect(() => {
    if (open && isAuthenticated && user?.email && !email) {
      setEmail(user.email);
    }
  }, [open, isAuthenticated, user?.email, email]);

  useEffect(() => {
    function handler(ev: Event) {
      const detail = (ev as CustomEvent).detail as
        | { category?: FeedbackCategory; prefill?: string }
        | undefined;
      if (detail?.category) setCategory(detail.category);
      if (detail?.prefill) setMessage((prev) => (prev.trim().length === 0 ? detail.prefill! : prev));
      setOpen(true);
    }
    window.addEventListener("marketlink:open-feedback", handler as EventListener);
    return () => window.removeEventListener("marketlink:open-feedback", handler as EventListener);
  }, []);

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          message,
          email: email || null,
          pageUrl: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
          appVersion: (import.meta as any).env?.VITE_APP_VERSION || null,
          diagnostics: await captureDiagnostics(),
          screenshots,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Thanks — feedback received." });
      setMessage("");
      setCategory("idea");
      setScreenshots([]);
      setOpen(false);
    },
    onError: (err: any) => {
      toast({ title: "Could not send feedback", description: err?.message || "Please try again.", variant: "destructive" });
    },
  });

  const disabled = submit.isPending || message.trim().length === 0;

  // Always-on feedback access for: demo admin (Mark), beta-testers,
  // and any signed-in user while demo mode is on. Hide only for the
  // public when demo mode is off — keeps the floating button out of
  // launch-day clutter while preserving the testing surface.
  const isDemoAdmin = Boolean((user as any)?.isDemoAdmin) && !recordingModeActive();
  const isBetaTester = Number((user as any)?.isBetaTester) === 1;
  if (config && config.demoMode === false && !isDemoAdmin && !isBetaTester) {
    return null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={onButtonClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label="Share feedback"
        className="fixed z-40 inline-flex h-10 touch-none select-none items-center gap-2 rounded-full bg-primary px-4 text-primary-foreground shadow-lg transition active:scale-95"
        style={
          position
            ? { left: position.x, top: position.y }
            : { bottom: "calc(70px + env(safe-area-inset-bottom, 0px))", left: 16 }
        }
      >
        <MessageSquare className="h-4 w-4" />
        <span className="text-sm font-medium">Feedback</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle>Share feedback</SheetTitle>
            <SheetDescription>
              What would you change or add? Your notes go straight to the product owner during the beta.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs">Type</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategory(opt.value)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      category === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {category === "bug" && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-200">
                For layout or visual bugs, take a screenshot first (Power + Volume Down on Android, or Side + Volume Up on iPhone), then describe what looks wrong.
              </p>
            )}

            <div>
              <Label htmlFor="feedback-message" className="text-xs">Message</Label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you're thinking…"
                className="mt-1 min-h-[120px]"
                maxLength={5000}
                autoFocus
              />
              <p className="mt-1 text-right text-[11px] text-muted-foreground">{message.length} / 5000</p>
            </div>

            <div>
              <Label className="text-xs">Screenshots (optional, up to {MAX_SCREENSHOTS})</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {screenshots.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={url}
                      alt={`Screenshot ${idx + 1}`}
                      className="h-16 w-16 rounded-md border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setScreenshots((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background text-foreground shadow-sm ring-1 ring-border hover:bg-muted"
                      aria-label="Remove screenshot"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {screenshots.length < MAX_SCREENSHOTS && (
                  <label className="inline-flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed text-muted-foreground hover:bg-muted">
                    {attaching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-5 w-5" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        void handleAttachFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Resized to ~{MAX_DIMENSION}px and compressed. Helps triage see exactly what you saw.
              </p>
            </div>

            {!isAuthenticated && (
              <div>
                <Label htmlFor="feedback-email" className="text-xs">Email (optional, only if you want a follow-up)</Label>
                <Input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                  maxLength={255}
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => submit.mutate()}
                disabled={disabled}
              >
                {submit.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send feedback"
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
